import {useLocation, useNavigate} from "react-router-dom";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Chess, DEFAULT_POSITION} from "chess.js";
import ChessBoard from "./ChessBoard";
import {fetchAnalysis, fetchAnalysisRange, fetchPgnAnalysis, fetchSingleMoveAnalysis} from "../clients/GameClient";
import Moves from "./Moves";
import EvalBar, {formatEval, isWhiteAhead} from "./EvalBar";
import {useUser} from "../UserContext";
import AnalysisChart from "./AnalysisChart";
import AnalysisOverview from "./AnalysisOverview";
import {rateMoves} from "../utils/analysisFunctions";
import {buildPositions} from "../utils/chessFunctions";
import {playMoveSound} from "../utils/sounds";
import PlayerStrip from "./PlayerStrip";
import Icon from "./ui/Icon";
import usePageTheme from "../utils/usePageTheme";

const evalRegex = /cp (-?\d+)/

// The backend sends centipawn scores as "cp 35" - convert those to pawns. Anything we cannot read becomes 0 rather
// than NaN, so one odd line never breaks the bar, the chart or the move ratings.
const parseAnalysisRow = (row) => {
    if (typeof row.eval === "string" && row.eval.includes("cp")) {
        const match = row.eval.match(evalRegex);
        const advantage = match ? parseInt(match[1], 10) : 0;
        return {...row, eval: Number.isNaN(advantage) ? 0 : advantage / 100};
    }
    return row;
}

// Converts the first move of a SAN line into an arrow on the board
const arrowForLine = (fen, line, color) => {
    if (!line || line.length === 0) return null;
    try {
        const chess = new Chess(fen);
        const move = chess.move(line[0]);
        return [move.from, move.to, color];
    } catch (e) {
        return null;
    }
};

// Remembers the eval/arrow toggles between visits (per browser, failing quietly if storage is blocked)
const readToggle = (key, fallback) => {
    try {
        const value = localStorage.getItem(key);
        return value === null ? fallback : value === "true";
    } catch (e) {
        return fallback;
    }
};
const writeToggle = (key, value) => {
    try {
        localStorage.setItem(key, String(value));
    } catch (e) {
        // ignore
    }
};

// The game is analysed in up to this many chunks, two at a time, so we can show progress. Every chunk starts its own
// Stockfish (which takes a moment), so we do not want the chunks to be too small either.
const MAX_CHUNKS = 8;
const MIN_CHUNK_SIZE = 4;
const PARALLEL_REQUESTS = 2;

const formatTimeLeft = (seconds) => {
    if (seconds < 10) return "a few seconds left";
    if (seconds < 60) return `about ${Math.round(seconds / 5) * 5} s left`;
    const minutes = Math.round(seconds / 60);
    return `about ${minutes} min left`;
};

const AnalysisProgress = ({progress}) => {
    const {done, total, startedAt} = progress;
    const percent = total ? Math.round((done / total) * 100) : 0;
    let timeLeft = "Estimating time…";
    if (done > 0) {
        const perPosition = (Date.now() - startedAt) / 1000 / done;
        timeLeft = formatTimeLeft(perPosition * (total - done));
    }
    return (
        <div className="analysis-progress" role="status" aria-live="polite">
            <div className="analysis-progress__top">
                <strong>Analysing… {percent}%</strong>
                <span className="small muted">{timeLeft}</span>
            </div>
            <div className="analysis-progress__bar" aria-hidden="true">
                <span style={{width: `${percent}%`}}/>
            </div>
            <span className="small muted">{done} of {total} positions analysed</span>
        </div>
    );
};

const NoGameToAnalyse = () => {
    const navigate = useNavigate();
    return (
        <div className="page page--narrow">
            <div className="card card-pad">
                <div className="empty-state">
                    <Icon name="sparkles"/>
                    <strong>No game selected</strong>
                    <span>Open a game from a profile, or paste a PGN on your profile page to analyse it.</span>
                    <button className="btn btn-primary" style={{marginTop: "0.6rem"}} onClick={() => navigate("/my-profile")}>Go to my profile</button>
                </div>
            </div>
        </div>
    );
};

const Analysis = () => {
    const location = useLocation()
    const state = location.state
    if (!state || !Array.isArray(state.moves)) return <NoGameToAnalyse/>;
    return <AnalysisView key={location.key} state={state}/>;
}

const AnalysisView = ({state}) => {
    usePageTheme("study");
    const navigate = useNavigate();
    const user = useUser();
    const moves = useMemo(() => state.moves.filter(Boolean), [state.moves]);
    const initialFen = state.initialFen || DEFAULT_POSITION;
    const gameId = state.gameId;
    const playerColor = state.playerColor === "black" ? "black" : "white";

    const [orientation, setOrientation] = useState(playerColor)
    const [ply, setPly] = useState(0)
    // A line the user explores away from the game: {fromPly, moves, positions, analysis, ratings}
    const [variation, setVariation] = useState(null)
    const variationRef = useRef(null);
    variationRef.current = variation;

    const [analysis, setAnalysis] = useState([])
    const [fetching, setFetching] = useState(false)
    const [analysisError, setAnalysisError] = useState(null)
    const [progress, setProgress] = useState(null)
    // Two separate switches: the evaluation (bar, score, best line, move badges) and the best-move arrow
    const [showEval, setShowEval] = useState(() => readToggle("analysis.showEval", true))
    const [showArrows, setShowArrows] = useState(() => readToggle("analysis.showArrows", false))
    const toggleEval = () => setShowEval((value) => { writeToggle("analysis.showEval", !value); return !value; });
    const toggleArrows = () => setShowArrows((value) => { writeToggle("analysis.showArrows", !value); return !value; });
    const unmounted = useRef(false);
    useEffect(() => {
        // (set to false on mount as well - React's strict mode mounts components twice in development)
        unmounted.current = false;
        return () => { unmounted.current = true; };
    }, []);

    const positions = useMemo(() => buildPositions(initialFen, moves), [initialFen, moves]);
    const ratings = useMemo(() => analysis.length > 1
        ? rateMoves(analysis.map((row) => row.eval), analysis.map((row) => row.turn))
        : [], [analysis]);
    const hasAnalysis = analysis.length > 0;

    // ----- Names -----
    const me = state.player || (user?.username ? {username: user.username, isActualUser: user.isLoggedIn} : null);
    const opponent = state.opponent;
    const whitePlayer = state.isPgnAnalysis ? {username: state.whiteName || "White"} : (playerColor === "white" ? me : opponent);
    const blackPlayer = state.isPgnAnalysis ? {username: state.blackName || "Black"} : (playerColor === "black" ? me : opponent);

    // ----- What is shown right now -----
    const inVariation = variation && variation.moves.length > 0;
    const current = inVariation ? variation.positions[variation.positions.length - 1] : positions[ply];
    const currentRow = inVariation ? variation.analysis[variation.moves.length - 1] : analysis[ply];
    const currentRating = inVariation ? variation.ratings[variation.moves.length - 1] : (ply > 0 ? ratings[ply - 1] : null);

    const badge = showEval && currentRating && current.move
        ? {square: current.move.to, symbol: currentRating.category.symbol}
        : null;
    const bestArrow = showArrows && currentRow ? arrowForLine(current.fen, currentRow.moves, "rgba(92, 139, 74, 0.8)") : null;

    // ----- Engine analysis -----
    // The game is sent to Stockfish in small chunks (two at a time), so we can show how far along we are.
    // If that fails for some reason we fall back to analysing the whole game in one request.
    const runAnalysis = async () => {
        const total = moves.length + 1;
        const results = new Array(total);
        const chunks = [];
        const chunkSize = Math.max(MIN_CHUNK_SIZE, Math.ceil(total / MAX_CHUNKS));
        for (let first = 0; first < total; first += chunkSize) {
            chunks.push([first, Math.min(first + chunkSize - 1, total - 1)]);
        }
        let next = 0;
        let done = 0;
        let failed = false;
        setFetching(true);
        setAnalysisError(null);
        setProgress({done: 0, total, startedAt: Date.now()});

        const analyseChunk = async ([first, last]) => {
            try {
                return await fetchAnalysisRange(initialFen, moves, first, last);
            } catch (e) {
                // one retry - a single slow chunk should not stop the whole analysis
                return await fetchAnalysisRange(initialFen, moves, first, last);
            }
        };
        const worker = async () => {
            while (!failed && !unmounted.current && next < chunks.length) {
                const chunk = chunks[next++];
                const rows = await analyseChunk(chunk);
                rows.forEach((row, offset) => { results[chunk[0] + offset] = parseAnalysisRow(row); });
                const finished = (done += rows.length);
                if (!unmounted.current) setProgress((previous) => previous && {...previous, done: finished});
            }
        };

        try {
            await Promise.all(Array.from({length: Math.min(PARALLEL_REQUESTS, chunks.length)}, worker));
            if (unmounted.current) return;
            if (results.some((row) => !row)) throw new Error("Some positions are missing from the analysis");
            setAnalysis(results);
        } catch (error) {
            failed = true;
            if (unmounted.current) return;
            setProgress(null);
            try {
                const fromPgn = () => fetchPgnAnalysis(initialFen, moves);
                const data = await (gameId ? fetchAnalysis(gameId).catch(fromPgn) : fromPgn());
                if (!unmounted.current) setAnalysis(data.map(parseAnalysisRow));
            } catch (fallbackError) {
                if (!unmounted.current) {
                    setAnalysisError(fallbackError.response?.data?.message || "Stockfish could not analyse this game right now.");
                }
            }
        } finally {
            if (!unmounted.current) {
                setFetching(false);
                setProgress(null);
            }
        }
    };

    const analyseVariation = useCallback((fromPly, variationMoves) => {
        if (!hasAnalysis) return;
        const sanSoFar = [...moves.slice(0, fromPly), ...variationMoves];
        fetchSingleMoveAnalysis(initialFen, sanSoFar)
            .then((data) => {
                const row = parseAnalysisRow(data[0]);
                setVariation((prev) => {
                    if (!prev || prev.fromPly !== fromPly || prev.moves.length !== variationMoves.length) return prev;
                    const index = variationMoves.length - 1;
                    const previousRow = index === 0 ? analysis[fromPly] : prev.analysis[index - 1];
                    const nextAnalysis = [...prev.analysis];
                    nextAnalysis[index] = row;
                    const nextRatings = [...prev.ratings];
                    if (previousRow) {
                        nextRatings[index] = rateMoves([previousRow.eval, row.eval], [previousRow.turn, row.turn])[0];
                    }
                    return {...prev, analysis: nextAnalysis, ratings: nextRatings};
                });
            })
            .catch(() => {
                // A failed single move analysis just means no eval for that move
            });
    }, [hasAnalysis, moves, initialFen, analysis]);

    // ----- Moving around -----
    // `silent` is used while an arrow key is held down, so the move sound does not machine-gun
    const plyRef = useRef(ply);
    plyRef.current = ply;
    const goToPly = useCallback((target, silent = false) => {
        const clamped = Math.max(0, Math.min(target, moves.length));
        const leavingVariation = variationRef.current && variationRef.current.moves.length > 0;
        if (clamped === plyRef.current && !leavingVariation) return;
        plyRef.current = clamped;
        setVariation(null);
        setPly(clamped);
        if (silent) return;
        const position = positions[clamped];
        if (position?.move) playMoveSound(position.move, position.inCheck);
    }, [moves.length, positions]);

    const handleMove = (move, newFen) => {
        playMoveSound(move, new Chess(newFen).inCheck());
        // Playing the game's own next move just steps forward along the game
        if (!inVariation && ply < moves.length && moves[ply] === move.san) {
            setPly(ply + 1);
            return;
        }
        const base = inVariation ? variation : {fromPly: ply, moves: [], positions: [positions[ply]], analysis: [], ratings: []};
        const nextMoves = [...base.moves, move.san];
        const nextVariation = {
            ...base,
            moves: nextMoves,
            positions: [...base.positions, {fen: newFen, move, inCheck: new Chess(newFen).inCheck()}]
        };
        setVariation(nextVariation);
        analyseVariation(base.fromPly, nextMoves);
    };

    const navigateMoves = useCallback((direction, silent = false) => {
        if (direction === "prev") {
            if (inVariation) {
                const remaining = variation.moves.length - 1;
                if (remaining === 0) {
                    setVariation(null);
                    setPly(variation.fromPly);
                } else {
                    setVariation({
                        ...variation,
                        moves: variation.moves.slice(0, remaining),
                        positions: variation.positions.slice(0, remaining + 1),
                        analysis: variation.analysis.slice(0, remaining),
                        ratings: variation.ratings.slice(0, remaining)
                    });
                }
                return;
            }
            goToPly(plyRef.current - 1, silent);
        } else if (direction === "next") {
            if (!inVariation) goToPly(plyRef.current + 1, silent);
        } else if (direction === "first") {
            goToPly(0, silent);
        } else if (direction === "last") {
            goToPly(moves.length, silent);
        }
    }, [inVariation, variation, goToPly, moves.length]);

    const navigateRef = useRef(navigateMoves);
    navigateRef.current = navigateMoves;
    useEffect(() => {
        const handleKey = (event) => {
            const tag = event.target.tagName;
            if (tag === "INPUT" || tag === "TEXTAREA") return;
            const map = {ArrowLeft: "prev", ArrowRight: "next", Home: "first", End: "last"};
            if (map[event.key]) {
                event.preventDefault();
                navigateRef.current(map[event.key], event.repeat);
            } else if (event.key === "f") {
                setOrientation((o) => (o === "white" ? "black" : "white"));
            }
        };
        window.addEventListener("keydown", handleKey)
        return () => window.removeEventListener("keydown", handleKey)
    }, []);

    const topColor = orientation === "white" ? "b" : "w";
    const bottomColor = orientation === "white" ? "w" : "b";
    const playerFor = (color) => (color === "w" ? whitePlayer : blackPlayer);
    const currentEval = currentRow?.eval;
    const evalLabel = formatEval(currentEval);

    const engineCard = (
        <div className="card">
            <div className="card-header">
                <div className="card-title"><Icon name="sparkles"/> Stockfish</div>

            </div>
            {hasAnalysis && (
                <div className="engine-toggles">
                    <div className="toggle-chips" role="group" aria-label="What to show">
                        <button type="button" className={`toggle-chip ${showEval ? "on" : ""}`} aria-pressed={showEval}
                                onClick={toggleEval} title="Evaluation bar, score, best line and move badges">
                            <Icon name="sparkles" size={14}/> Evaluation
                        </button>
                        <button type="button" className={`toggle-chip ${showArrows ? "on" : ""}`} aria-pressed={showArrows}
                                onClick={toggleArrows} title="Show the best move as an arrow on the board">
                            <Icon name="arrowRight" size={14}/> Arrows
                        </button>
                    </div>
                </div>
            )}
            {!hasAnalysis ? (
                <div className="analysis-cta">
                    <div className="analysis-cta__icon"><Icon name="sparkles"/></div>
                    {user?.isLoggedIn ? (
                        <>
                            <div>
                                <strong>Analyse the whole game</strong>
                                <p className="small muted">Find the best moves, mistakes and blunders. This can take a little while.</p>
                            </div>
                            {progress ? <AnalysisProgress progress={progress}/> : (
                                <button className="btn btn-primary" onClick={runAnalysis} disabled={fetching}>
                                    {fetching ? <><span className="spinner spinner--sm spinner--light"/> Analysing…</> : <>Run analysis</>}
                                </button>
                            )}
                        </>
                    ) : (
                        <>
                            <div>
                                <strong>Engine analysis needs an account</strong>
                                <p className="small muted">You can still step through the game and try your own moves.</p>
                            </div>
                            <button className="btn btn-secondary" onClick={() => navigate("/my-profile")}>
                                <Icon name="login"/> Log in
                            </button>
                        </>
                    )}
                    {analysisError && <div className="form-error">{analysisError}</div>}
                </div>
            ) : (
                <>
                    {!showEval && (
                        <div className="card-body small muted">
                            The evaluation is hidden. Turn on <strong>Evaluation</strong> above to see scores and the best line.
                        </div>
                    )}
                    {showEval && <div className="card-body stack stack--sm">
                        <div className="row" style={{justifyContent: "space-between"}}>
                            <span className="small muted" style={{fontWeight: 700}}>Evaluation</span>
                            {currentRow ? (
                                <span className={`eval-pill ${isWhiteAhead(currentEval) ? "eval-pill--white" : "eval-pill--black"}`}>{evalLabel}</span>
                            ) : <span className="spinner spinner--sm"/>}
                        </div>
                        {currentRating && (
                            <div className="row small" style={{fontWeight: 700}}>
                                <span style={{width: 18, height: 18, display: "inline-flex"}}>{currentRating.category.symbol(18)}</span>
                                {current.move?.san} was {/^[AEIOU]/i.test(currentRating.category.title) ? "an" : "a"} {currentRating.category.title.toLowerCase()}
                            </div>
                        )}
                    </div>}
                    {showEval && currentRow?.moves?.length > 0 && (
                        <div className="best-line">
                            <span className="small muted" style={{fontWeight: 700}}>Best line</span>
                            <div className="best-line__moves">
                                {currentRow.moves.slice(0, 8).map((san, i) => <span key={i}>{san}</span>)}
                            </div>
                        </div>
                    )}
                    <AnalysisChart data={analysis.map((row) => row.eval)} currentIndex={inVariation ? -1 : ply} onPointClick={goToPly}/>
                </>
            )}
        </div>
    );

    return (
        <div className="page">
            <div className="page-header">
                <div>
                    <div className="eyebrow">{state.isPgnAnalysis ? "PGN analysis" : "Game review"}</div>
                    <h1 style={{fontSize: "clamp(1.4rem, 2.6vw, 1.9rem)"}}>
                        {whitePlayer?.username || "White"} <span className="muted" style={{fontStyle: "italic", fontWeight: 500}}>vs</span> {blackPlayer?.username || "Black"}
                    </h1>
                </div>
            </div>

            <div className="analysis-layout">
                <div className="analysis-layout__board">
                    <div className="board-column" style={{maxWidth: "min(100%, 82vh)"}}>
                        <PlayerStrip user={playerFor(topColor)} color={topColor} fen={current.fen}/>
                        <div className="board-with-eval">
                            {hasAnalysis && showEval && <EvalBar evaluation={currentEval} orientation={orientation}/>}
                            <ChessBoard
                                id="analysis-board"
                                fen={current.fen}
                                orientation={orientation}
                                onMove={handleMove}
                                lastMove={current.move ? {from: current.move.from, to: current.move.to} : null}
                                arrows={bestArrow ? [bestArrow] : []}
                                badge={badge}
                            />
                        </div>
                        <PlayerStrip user={playerFor(bottomColor)} color={bottomColor} fen={current.fen}/>
                    </div>
                </div>

                <aside className="analysis-layout__panel">
                    <div className="card panel">
                        <div className="game-status">
                            <Icon name="clock"/>
                            <div className="game-status__text">
                                <div>{inVariation ? "Exploring your own line" : ply === 0 ? "Starting position" : `Move ${Math.ceil(ply / 2)} · ${moves[ply - 1]}`}</div>
                                <div className="game-status__sub">Use ← → to step through the game, or move the pieces to try ideas</div>
                            </div>
                        </div>
                        <div style={{borderTop: "1px solid var(--line)", display: "flex", flexDirection: "column", minHeight: 0, flex: 1}}>
                            <Moves
                                moves={moves}
                                initialFen={initialFen}
                                currentPly={ply}
                                onSelect={goToPly}
                                ratings={hasAnalysis ? ratings : undefined}
                                variation={inVariation ? {fromPly: variation.fromPly, moves: variation.moves} : null}
                                onClearVariation={() => goToPly(variation?.fromPly ?? ply)}
                                onNavigate={navigateMoves}
                                canPrev={inVariation || ply > 0}
                                canNext={!inVariation && ply < moves.length}
                                emptyText="This game has no moves"
                                extraControls={
                                    <button className="icon-btn" onClick={() => setOrientation((o) => (o === "white" ? "black" : "white"))}
                                            aria-label="Flip board" title="Flip board (F)">
                                        <Icon name="flip"/>
                                    </button>
                                }
                            />
                        </div>
                    </div>
                </aside>

                <aside className="analysis-layout__summary stack">
                    {engineCard}
                    {hasAnalysis && (
                        <div className="card">
                            <div className="card-header">
                                <div className="card-title"><Icon name="trophy"/> Move quality</div>
                            </div>
                            <div className="card-body">
                                <AnalysisOverview
                                    whiteMoveRatings={ratings.filter((rating) => rating.color === "w")}
                                    blackMoveRatings={ratings.filter((rating) => rating.color === "b")}
                                    whiteName={whitePlayer?.username}
                                    blackName={blackPlayer?.username}
                                />
                            </div>
                        </div>
                    )}
                </aside>
            </div>
        </div>
    )
}

export default Analysis
